import React from 'react';
import { Globe, Rocket, TrendingUp, CheckCircle } from 'lucide-react';

function AmericasGoingGlobal() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          Americas (US, Canada, LATAM) DTC Brands Going Global
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          You've built a successful DTC brand in the Americas. Now it's time to expand globally. But international expansion comes with complexity—from market selection to logistics, compliance, and localization.
        </p>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">The Global Expansion Challenge</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Market Selection Complexity</h3>
              <p className="text-gray-700">Choosing the right markets requires deep understanding of local consumer behavior, competitive landscapes, and regulatory environments.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">International Logistics</h3>
              <p className="text-gray-700">Managing global supply chains, customs, duties, and last-mile delivery across multiple countries is complex and costly.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Local Market Knowledge</h3>
              <p className="text-gray-700">Understanding cultural nuances, local payment preferences, and market-specific consumer expectations.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold mb-2 text-[#000000]">Resource Constraints</h3>
              <p className="text-gray-700">Building in-market teams, establishing local partnerships, and managing multiple operations simultaneously.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Our Global Expansion Solutions</h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Strategic Market Entry</h3>
                <p className="text-gray-700 mb-3">We help you identify and prioritize the best markets for expansion based on your product, brand, and business model.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Market opportunity assessment and prioritization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Competitive analysis and positioning strategy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Regulatory compliance and legal framework setup</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <Rocket size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Full-Service Market Execution</h3>
                <p className="text-gray-700 mb-3">Choose our joint venture model where we build together, or our advisory model where we handle sales, marketing, and supply chain.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">In-market sales and distribution management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Localized marketing campaigns and brand building</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Supply chain setup and logistics management</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#000000]">Scale with Confidence</h3>
                <p className="text-gray-700 mb-3">Our proven playbooks and local expertise help you scale efficiently across multiple markets simultaneously.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Multi-market launch coordination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Performance tracking and optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-[#BB3E00] mt-1 flex-shrink-0" />
                    <span className="text-sm">Local team building and management</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#38A3A5] to-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Go Global?</h2>
          <p className="mb-6">Let's map out your international expansion strategy and accelerate your global growth.</p>
          <a
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all"
          >
            Schedule a Consult
          </a>
        </section>
      </div>
    </div>
  );
}

export default AmericasGoingGlobal;
